import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Space,
  message,
  Spin,
  Statistic
} from 'antd';
import { BarChartOutlined, ReloadOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import indicatorService from '../services/indicatorService';

const { Title, Text } = Typography;

const Indicators = () => {
  const [ismPMIData, setIsmPMIData] = useState(null);
  const [fearGreedData, setFearGreedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fearGreedLoading, setFearGreedLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fearGreedError, setFearGreedError] = useState(null);

  useEffect(() => {
    fetchISMPMIData();
    fetchFearGreedData();
  }, []);

  const fetchISMPMIData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await indicatorService.getISMPMI();
      setIsmPMIData(data);
      message.success('ISM PMI 데이터를 성공적으로 로드했습니다.');
    } catch (err) {
      setError(err.message);
      console.error('Error fetching ISM PMI data:', err);
      message.error('ISM PMI 데이터 로드에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFearGreedData = async () => {
    try {
      setFearGreedLoading(true);
      setFearGreedError(null);
      const data = await indicatorService.getFearAndGreedIndex({ limit: 365 });
      setFearGreedData(data);
      message.success('Fear & Greed Index 데이터를 성공적으로 로드했습니다.');
    } catch (err) {
      setFearGreedError(err.message);
      console.error('Error fetching Fear and Greed data:', err);
      message.error('Fear & Greed Index 데이터 로드에 실패했습니다.');
    } finally {
      setFearGreedLoading(false);
    }
  };

  const formatChartData = (data) => {
    if (!data || !data.data) return [];
    
    return data.data.map(item => ({
      x: item.period_start,
      y: item.value
    }));
  };

  const getPMIStatus = (value) => {
    if (value > 50) {
      return { status: 'expansion', color: 'green', arrow: 'increase' };
    } else if (value < 50) {
      return { status: 'contraction', color: 'red', arrow: 'decrease' };
    } else {
      return { status: 'neutral', color: 'gray', arrow: null };
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        {/* Header and Controls */}
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <BarChartOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#1890ff' }} />
                <Title level={2} style={{ display: 'inline', margin: 0 }}>
                  Economic Indicators
                </Title>
              </div>
              
              <Row gutter={[16, 16]} align="top">
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <Text strong>ISM Manufacturing PMI</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">
                        Institute for Supply Management의 제조업 구매관리자지수
                      </Text>
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <Text strong>Data Source</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">DB.nomics API</Text>
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={12} md={8}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={fetchISMPMIData}
                      loading={loading}
                    >
                      Refresh Data
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>

        {/* Statistics */}
        {ismPMIData && (
          <Col span={24}>
            <Card title="ISM PMI Overview" size="small">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Latest Value"
                    value={ismPMIData.latest_value}
                    valueStyle={{
                      color: getPMIStatus(ismPMIData.latest_value).color === 'green' ? '#3f8600' : 
                             getPMIStatus(ismPMIData.latest_value).color === 'red' ? '#cf1322' : '#666'
                    }}
                    prefix={
                      getPMIStatus(ismPMIData.latest_value).arrow === 'increase' ? <ArrowUpOutlined /> :
                      getPMIStatus(ismPMIData.latest_value).arrow === 'decrease' ? <ArrowDownOutlined /> : null
                    }
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Latest Period"
                    value={ismPMIData.latest_period}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Data Points"
                    value={ismPMIData.data ? ismPMIData.data.length : 0}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Status"
                    value={getPMIStatus(ismPMIData.latest_value).status === 'expansion' ? 'Expansion' : 
                           getPMIStatus(ismPMIData.latest_value).status === 'contraction' ? 'Contraction' : 'Neutral'}
                    valueStyle={{
                      color: getPMIStatus(ismPMIData.latest_value).color === 'green' ? '#3f8600' : 
                             getPMIStatus(ismPMIData.latest_value).color === 'red' ? '#cf1322' : '#666'
                    }}
                  />
                </Col>
              </Row>
              <div style={{ marginTop: '16px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  * PMI 50 이상: 경제 확장, 50 미만: 경제 수축을 의미합니다.
                </Text>
              </div>
            </Card>
          </Col>
        )}

        {/* Chart Content */}
        <Col span={24}>
          {loading ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '100px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                  <Text>Loading ISM PMI data...</Text>
                </div>
              </div>
            </Card>
          ) : error ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '100px' }}>
                <BarChartOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Title level={4} type="secondary">Data Loading Failed</Title>
                <Text type="secondary">{error}</Text>
                <div style={{ marginTop: '16px' }}>
                  <Button type="primary" onClick={fetchISMPMIData}>
                    Try Again
                  </Button>
                </div>
              </div>
            </Card>
          ) : ismPMIData ? (
            <Card title="ISM Manufacturing PMI Trend" extra={<Text type="secondary">{ismPMIData.provider}</Text>}>
              <div style={{ height: '400px' }}>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'line',
                      height: 350,
                      toolbar: {
                        show: false,
                      },
                    },
                    title: {
                      text: 'ISM Manufacturing PMI',
                      align: 'left',
                      style: {
                        fontSize: '16px',
                        color: '#666'
                      }
                    },
                    stroke: {
                      curve: 'smooth',
                      width: 3,
                      colors: ['#1890ff']
                    },
                    xaxis: {
                      // {x, y} 데이터 형식 사용 시 categories 제거 - x값에서 자동 추출
                      type: 'datetime',
                      labels: {
                        style: {
                          colors: '#8c8c8c',
                          fontSize: '12px',
                        },
                      },
                    },
                    yaxis: {
                      title: {
                        text: 'PMI Value',
                        style: {
                          color: '#8c8c8c',
                        },
                      },
                      labels: {
                        style: {
                          colors: '#8c8c8c',
                        },
                      },
                      min: 40,
                      max: 70,
                    },
                    grid: {
                      borderColor: '#f1f1f1',
                      strokeDashArray: 4,
                    },
                    tooltip: {
                      x: {
                        format: 'yyyy-MM'
                      },
                      y: {
                        formatter: function (val) {
                          return val.toFixed(1);
                        }
                      }
                    },
                    colors: ['#1890ff'],
                    markers: {
                      size: 4,
                      colors: ['#1890ff'],
                      strokeColors: '#fff',
                      strokeWidth: 2,
                      hover: {
                        size: 6,
                      }
                    },
                    annotations: {
                      yaxis: [{
                        y: 50,
                        borderColor: '#ff4d4f',
                        borderWidth: 2,
                        strokeDashArray: 5,
                        label: {
                          text: 'Expansion/Contraction Line (50)',
                          position: 'right',
                          style: {
                            color: '#ff4d4f',
                            background: '#fff'
                          }
                        }
                      }]
                    }
                  }}
                  series={[{
                    name: 'PMI',
                    data: ismPMIData.data.map(item => ({
                      x: item.period_start,
                      y: item.value
                    }))
                  }]}
                  type="line"
                  height={350}
                />
              </div>
            </Card>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '100px' }}>
                <BarChartOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Title level={4} type="secondary">No Data Available</Title>
                <Text type="secondary">Please try refreshing the data.</Text>
              </div>
            </Card>
          )}
        </Col>

        {/* Fear and Greed Index Section */}
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <BarChartOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#ff4d4f' }} />
                <Title level={2} style={{ display: 'inline', margin: 0 }}>
                  CNN Fear & Greed Index
                </Title>
              </div>
              
              <Row gutter={[16, 16]} align="top">
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <Text strong>Market Sentiment Indicator</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">
                        시장의 공포와 탐욕 수준을 0-100 스케일로 측정
                      </Text>
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <Text strong>Data Source</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">CNN Business API</Text>
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={12} md={8}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={fetchFearGreedData}
                      loading={fearGreedLoading}
                      danger
                    >
                      Refresh Data
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>

        {/* Fear and Greed Statistics */}
        {fearGreedData && fearGreedData.latest && (
          <Col span={24}>
            <Card title="Fear & Greed Index Overview" size="small">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Current Value"
                    value={fearGreedData.latest.value}
                    valueStyle={{
                      color: fearGreedData.latest.value >= 75 ? '#cf1322' : 
                             fearGreedData.latest.value >= 55 ? '#fa8c16' :
                             fearGreedData.latest.value >= 45 ? '#faad14' :
                             fearGreedData.latest.value >= 25 ? '#52c41a' : '#3f8600'
                    }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Rating"
                    value={fearGreedData.latest.rating}
                    valueStyle={{ 
                      color: fearGreedData.latest.value >= 75 ? '#cf1322' : 
                             fearGreedData.latest.value >= 55 ? '#fa8c16' :
                             fearGreedData.latest.value >= 45 ? '#faad14' :
                             fearGreedData.latest.value >= 25 ? '#52c41a' : '#3f8600'
                    }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Latest Date"
                    value={fearGreedData.latest.date}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Data Points"
                    value={fearGreedData.count}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
              </Row>
              <div style={{ marginTop: '16px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  * 0-25: Extreme Fear (극단적 공포), 25-45: Fear (공포), 45-55: Neutral (중립), 55-75: Greed (탐욕), 75-100: Extreme Greed (극단적 탐욕)
                </Text>
              </div>
            </Card>
          </Col>
        )}

        {/* Fear and Greed Chart */}
        <Col span={24}>
          {fearGreedLoading ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '100px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                  <Text>Loading Fear & Greed Index data...</Text>
                </div>
              </div>
            </Card>
          ) : fearGreedError ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '100px' }}>
                <BarChartOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Title level={4} type="secondary">Data Loading Failed</Title>
                <Text type="secondary">{fearGreedError}</Text>
                <div style={{ marginTop: '16px' }}>
                  <Button type="primary" danger onClick={fetchFearGreedData}>
                    Try Again
                  </Button>
                </div>
              </div>
            </Card>
          ) : fearGreedData && fearGreedData.data ? (
            <Card title="CNN Fear & Greed Index Trend" extra={<Text type="secondary">CNN Business</Text>}>
              <div style={{ height: '400px' }}>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'area',
                      height: 350,
                      toolbar: {
                        show: false,
                      },
                    },
                    title: {
                      text: 'Fear & Greed Index (Past Year)',
                      align: 'left',
                      style: {
                        fontSize: '16px',
                        color: '#666'
                      }
                    },
                    stroke: {
                      curve: 'smooth',
                      width: 3,
                    },
                    fill: {
                      type: 'gradient',
                      gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.7,
                        opacityTo: 0.2,
                      }
                    },
                    xaxis: {
                      // {x, y} 데이터 형식 사용 시 categories 제거 - x값에서 자동 추출
                      type: 'datetime',
                      labels: {
                        style: {
                          colors: '#8c8c8c',
                          fontSize: '12px',
                        },
                      },
                    },
                    yaxis: {
                      title: {
                        text: 'Index Value',
                        style: {
                          color: '#8c8c8c',
                        },
                      },
                      labels: {
                        style: {
                          colors: '#8c8c8c',
                        },
                      },
                      min: 0,
                      max: 100,
                    },
                    grid: {
                      borderColor: '#f1f1f1',
                      strokeDashArray: 4,
                    },
                    tooltip: {
                      x: {
                        format: 'yyyy-MM-dd'
                      },
                      y: {
                        formatter: function (val) {
                          return val.toFixed(1);
                        }
                      }
                    },
                    colors: ['#ff4d4f'],
                    markers: {
                      size: 3,
                      strokeColors: '#fff',
                      strokeWidth: 2,
                      hover: {
                        size: 5,
                      }
                    },
                    annotations: {
                      yaxis: [
                        {
                          y: 25,
                          borderColor: '#52c41a',
                          borderWidth: 1,
                          strokeDashArray: 5,
                          label: {
                            text: 'Fear (25)',
                            position: 'left',
                            style: {
                              color: '#52c41a',
                              background: '#fff',
                              fontSize: '10px'
                            }
                          }
                        },
                        {
                          y: 45,
                          borderColor: '#faad14',
                          borderWidth: 1,
                          strokeDashArray: 5,
                          label: {
                            text: 'Neutral (45)',
                            position: 'left',
                            style: {
                              color: '#faad14',
                              background: '#fff',
                              fontSize: '10px'
                            }
                          }
                        },
                        {
                          y: 55,
                          borderColor: '#fa8c16',
                          borderWidth: 1,
                          strokeDashArray: 5,
                          label: {
                            text: 'Greed (55)',
                            position: 'left',
                            style: {
                              color: '#fa8c16',
                              background: '#fff',
                              fontSize: '10px'
                            }
                          }
                        },
                        {
                          y: 75,
                          borderColor: '#cf1322',
                          borderWidth: 1,
                          strokeDashArray: 5,
                          label: {
                            text: 'Extreme Greed (75)',
                            position: 'left',
                            style: {
                              color: '#cf1322',
                              background: '#fff',
                              fontSize: '10px'
                            }
                          }
                        }
                      ]
                    }
                  }}
                  series={[{
                    name: 'Fear & Greed Index',
                    data: fearGreedData.data.map(item => ({
                      x: item.date,
                      y: item.value
                    }))
                  }]}
                  type="area"
                  height={350}
                />
              </div>
            </Card>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '100px' }}>
                <BarChartOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Title level={4} type="secondary">No Data Available</Title>
                <Text type="secondary">Please try refreshing the data.</Text>
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Indicators;
